/**
 * Client-side feature flag support for WeCo Suite.
 *
 * Fetches flags from /api/people/api/v1.0/config/features/ and provides
 * a React hook for conditional rendering.
 *
 * Usage:
 *   const canExport = useFeatureFlag("advanced_exports");
 *   if (canExport) showButton();
 */

import { createContext, useContext } from "react";

/** Shape of the feature flags context. */
export interface FeatureFlagsState {
  flags: Record<string, boolean>;
  loaded: boolean;
}

/** Default (empty) state — all flags false until loaded. */
export const DEFAULT_FLAGS_STATE: FeatureFlagsState = {
  flags: {},
  loaded: false,
};

/** React context holding current feature flags. */
export const FeatureFlagsContext = createContext<FeatureFlagsState>(DEFAULT_FLAGS_STATE);

/**
 * Hook to check if a feature flag is enabled for the current user/org.
 *
 * Returns `false` until flags are loaded (safe for SSR/hydration).
 *
 * @param key - The flag key (e.g., "advanced_exports")
 * @returns boolean — whether the flag is enabled
 */
export function useFeatureFlag(key: string): boolean {
  const { flags, loaded } = useContext(FeatureFlagsContext);
  if (!loaded) return false;
  return flags[key] ?? false;
}

/**
 * Hook to access the full flags map.
 */
export function useFeatureFlags(): FeatureFlagsState {
  return useContext(FeatureFlagsContext);
}

/** API path for fetching feature flags. */
export const FEATURE_FLAGS_API = "/api/people/api/v1.0/config/features/";

/**
 * Fetch feature flags from the People service.
 *
 * @param getToken — async function returning a valid access token
 * @returns Record<string, boolean> map of flag key → enabled
 */
export async function fetchFeatureFlags(
  getToken: () => Promise<string | null>
): Promise<Record<string, boolean>> {
  const token = await getToken();
  if (!token) return {};

  const res = await fetch(FEATURE_FLAGS_API, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) return {};

  const data = await res.json();
  return data.flags ?? {};
}
